<template>
  <div v-if="total > 0" class="results-bar">
    <div class="results-bar-inner">
      <div class="results-summary">
        <fa :icon="icon" class="results-icon" />
        <span class="results-count">{{ total }}</span>
        <span class="results-label">{{ label }}</span>
        <span v-if="originalTotal && originalTotal > total" class="results-filtered">
          (af {{ originalTotal }})
        </span>
      </div>
      <div class="results-progress" v-if="showProgress">
        <div class="results-progress-track">
          <div class="results-progress-fill" :style="{ width: pct + '%' }"></div>
        </div>
        <span class="results-progress-label">{{ visible }} / {{ total }}</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ResultsBar',
  props: {
    // Total result count (after filtering).
    total: { type: Number, required: true },
    // The unfiltered total. When provided, renders "(af X)" next to the count
    // when filtering is in effect. Optional.
    originalTotal: { type: Number, default: 0 },
    // Singular noun rendered after the count, e.g. "resultater".
    label: { type: String, default: 'resultater' },
    // FontAwesome icon name (defaults to "list").
    icon: { type: String, default: 'list' },
    // CSS selector for the tile elements in the current listing - used to
    // compute the scroll position by tile geometry. Without it the bar still
    // shows the count, just not the progress.
    tileSelector: { type: String, default: '' }
  },
  data() {
    return {
      visible: 0
    }
  },
  computed: {
    pct() {
      return this.total === 0 ? 0 : (this.visible / this.total) * 100
    },
    showProgress() {
      return !!this.tileSelector && this.total > 10
    }
  },
  watch: {
    // When the result set changes (search query, filter), re-measure on the
    // next tick so we read the new DOM positions, not the stale ones.
    total() {
      this.scheduleMeasure()
    }
  },
  methods: {
    measure() {
      // "Visible" = the highest-indexed tile whose top has entered (or passed)
      // the viewport's bottom edge. That's "how far through the list you are",
      // measured in tile positions. It's based purely on geometry of rendered
      // tiles, so auto-loading more tiles below doesn't shift the number.
      if (!this.tileSelector) {
        this.visible = 0
        return
      }
      const tiles = document.querySelectorAll(this.tileSelector)
      if (tiles.length === 0) {
        this.visible = 0
        return
      }
      const vpBottom = window.innerHeight
      let last = 0
      for (let i = 0; i < tiles.length; i++) {
        const top = tiles[i].getBoundingClientRect().top
        if (top < vpBottom) last = i + 1
        // Tiles are in DOM order; once we find one fully below the viewport
        // every later one is too (assuming a vertical flow / grid).
        else break
      }
      this.visible = Math.min(last, this.total)
    },
    scheduleMeasure() {
      // Run after layout settles - the new tiles may not be mounted yet.
      this.$nextTick(() => requestAnimationFrame(this.measure))
    }
  },
  mounted() {
    this.scheduleMeasure()
    let ticking = false
    this._onScroll = () => {
      if (ticking) return
      ticking = true
      requestAnimationFrame(() => {
        this.measure()
        ticking = false
      })
    }
    window.addEventListener('scroll', this._onScroll, { passive: true })
    window.addEventListener('resize', this._onScroll, { passive: true })
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this._onScroll)
    window.removeEventListener('resize', this._onScroll)
  }
}
</script>

<style lang="scss" scoped>
// The bar floats just below the fixed nav. Sticky-position keeps it pinned
// when the user scrolls past it; in normal flow it sits where rendered.
//
// Width math: max-width 1280 + horizontal padding 20 -> effective content
// width 1240. That matches .container content width so the bar's white card
// lines up edge-to-edge with the tiles below.
// Plain white card. Sticky with a clear 10px gap below the nav when stuck,
// 20px margin above/below when in normal flow. No blur, no translucency -
// content that scrolls beneath simply shows in the page background gutter
// outside the bar's max-width (the bar's grid aligns with the recipe grid
// below, so cards never actually scroll past the white card horizontally).
.results-bar {
  position: sticky;
  top: 75px;  // 65px nav + 10px breathing room
  z-index: 2;

  // Width math: max 1240, with a 20px gutter on each side so the bar's
  // outer edges line up with the recipe grid below (which lives inside a
  // .container with 20px horizontal padding).
  max-width: 1240px;
  width: calc(100% - 40px);
  // 20px breathing above and below when in normal flow. Sticky `top` takes
  // over once stuck, so this margin only shows pre-scroll.
  margin: 20px auto;
  box-sizing: border-box;

  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.results-bar-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 10px 16px;
}

.results-summary {
  display: inline-flex;
  align-items: baseline;
  gap: 8px;
  color: var(--dark);
}

.results-icon {
  color: var(--main);
  font-size: 0.95rem;
  align-self: center;
}

.results-count {
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--darker);
  font-variant-numeric: tabular-nums;
}

.results-label {
  font-size: 0.88rem;
  font-weight: 500;
  color: var(--dark);
  opacity: 0.75;
  letter-spacing: 0.02em;
}

.results-filtered {
  font-size: 0.78rem;
  color: #888;
  margin-left: 2px;
}

.results-progress {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-width: 140px;
}

.results-progress-track {
  width: 140px;
  height: 4px;
  background: rgba(0, 0, 0, 0.08);
  border-radius: 999px;
  overflow: hidden;
}

.results-progress-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--main), var(--darker));
  border-radius: 999px;
  transition: width 0.15s linear;
}

.results-progress-label {
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--dark);
  opacity: 0.7;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.02em;
  min-width: 56px;
  text-align: right;
}

// Smaller screens: keep both the count and the X/Y progress visible -
// they're the whole point of the bar. We shrink the progress track instead
// of hiding the label, and only collapse the label at really tight widths.
@media (max-width: 700px) {
  .results-bar-inner { padding: 8px 12px; gap: 10px; }
  .results-progress { min-width: auto; gap: 8px; }
  .results-progress-track { width: 80px; }
  .results-progress-label { min-width: 0; font-size: 0.72rem; }
}
@media (max-width: 480px) {
  .results-bar { padding: 0 12px; } // tighter side gutter but never zero
  .results-progress-track { width: 60px; }
  .results-label { font-size: 0.8rem; }
  .results-filtered { display: none; } // free up some room
}
@media (max-width: 340px) {
  .results-progress-track { width: 44px; }
  .results-progress-label { display: none; } // last-resort hide
}
</style>
